/**
 * GroupTasksPanel — Kanban-style overview embedded in the Tasks tab.
 *
 * Each column is a TaskList with its tasks. The list header supports:
 *   - inline rename (click the pencil)
 *   - a "View" link to the standalone TaskListDetailPage where the
 *     Kanban / Table views live with drag-and-drop and edit/delete
 *
 * Task cards click to open the shared TaskEditModal.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import {
  ApiClientError,
  groupTasksApi,
  type GroupTask,
  type GroupTaskList,
} from '@/services/api';
import { TaskEditModal, TASK_STATUS_LABEL, TASK_STATUS_PILL } from './TaskEditModal';

interface Props {
  groupId: string;
}

export function GroupTasksPanel({ groupId }: Props) {
  const params = useParams<{ chapterSlug?: string }>();
  const chapterSlug = params.chapterSlug ?? '96';
  const [lists, setLists] = useState<GroupTaskList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newListName, setNewListName] = useState('');
  const [busy, setBusy] = useState(false);
  const [openTask, setOpenTask] = useState<GroupTask | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await groupTasksApi.listLists(groupId);
      setLists(data);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleAddList(e: FormEvent) {
    e.preventDefault();
    if (!newListName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await groupTasksApi.createList(groupId, newListName.trim());
      setNewListName('');
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteList(list: GroupTaskList) {
    if (!window.confirm(`Delete list "${list.name}" and all its tasks?`)) return;
    try {
      await groupTasksApi.deleteList(list.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="space-y-3 p-3">
      <form className="flex items-end gap-2" onSubmit={handleAddList}>
        <div className="flex-1">
          <label className="block text-xs text-white/60 mb-1">New task list</label>
          <input
            className="input"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            placeholder="e.g. Prep Work"
            maxLength={200}
          />
        </div>
        <button type="submit" className="btn-primary text-sm px-3 py-1.5" disabled={busy || !newListName.trim()}>
          + List
        </button>
      </form>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {loading ? (
        <p className="text-white/60 text-sm">Loading…</p>
      ) : lists.length === 0 ? (
        <div className="card text-center py-8 text-white/60">
          <p className="text-base text-white mb-1">No task lists yet</p>
          <p className="text-sm">Add the first list above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {lists.map((list) => (
            <ListColumn
              key={list.id}
              list={list}
              chapterSlug={chapterSlug}
              onChanged={reload}
              onOpenTask={setOpenTask}
              onDeleteList={() => handleDeleteList(list)}
            />
          ))}
        </div>
      )}

      {openTask && (
        <TaskEditModal
          task={openTask}
          onClose={() => setOpenTask(null)}
          onSaved={() => { setOpenTask(null); void reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-list column with inline rename + task creation.
// ---------------------------------------------------------------------------

function ListColumn({
  list,
  chapterSlug,
  onChanged,
  onOpenTask,
  onDeleteList,
}: {
  list: GroupTaskList;
  chapterSlug: string;
  onChanged: () => void;
  onOpenTask: (t: GroupTask) => void;
  onDeleteList: () => void;
}) {
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline rename
  const [editingName, setEditingName] = useState(false);
  const [pendingName, setPendingName] = useState(list.name);
  const [renaming, setRenaming] = useState(false);

  async function handleAddTask(e: FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await groupTasksApi.createTask(list.id, { title: newTitle.trim() });
      setNewTitle('');
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRename(e?: FormEvent) {
    if (e) e.preventDefault();
    const name = pendingName.trim();
    if (!name || name === list.name) {
      setEditingName(false);
      setPendingName(list.name);
      return;
    }
    setRenaming(true);
    setError(null);
    try {
      await groupTasksApi.renameList(list.id, name);
      setEditingName(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Rename failed');
    } finally {
      setRenaming(false);
    }
  }

  return (
    <section className="card flex flex-col">
      <header className="flex items-center justify-between mb-2 gap-2">
        {editingName ? (
          <form onSubmit={handleRename} className="flex items-center gap-2 flex-1">
            <input
              className="input py-1 text-sm flex-1"
              value={pendingName}
              autoFocus
              maxLength={200}
              onChange={(e) => setPendingName(e.target.value)}
              onBlur={() => handleRename()}
            />
            <button
              type="submit"
              disabled={renaming}
              className="text-xs px-2 py-1 bg-primary/20 text-primary rounded border border-primary/30"
            >
              {renaming ? '…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditingName(false); setPendingName(list.name); }}
              className="text-xs text-white/60 hover:text-white"
            >
              Cancel
            </button>
          </form>
        ) : (
          <h3 className="text-white font-semibold flex items-center gap-2 min-w-0">
            <span className="truncate">{list.name}</span>
            <button
              type="button"
              onClick={() => { setPendingName(list.name); setEditingName(true); }}
              className="text-white/40 hover:text-white"
              aria-label="Rename list"
              title="Rename list"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </h3>
        )}
        <div className="flex items-center gap-2 text-xs flex-shrink-0">
          <Link
            to={`/c/${chapterSlug}/m/tasks/${list.id}`}
            className="text-primary hover:text-primary-light"
            title="Open list"
          >
            View →
          </Link>
          <button
            type="button"
            onClick={onDeleteList}
            className="text-red-400 hover:text-red-300"
            title="Delete list"
          >
            Delete list
          </button>
        </div>
      </header>

      <form className="flex gap-2 mb-3" onSubmit={handleAddTask}>
        <input
          className="input text-sm flex-1 py-1.5"
          placeholder="New task title…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          maxLength={500}
        />
        <button type="submit" disabled={busy || !newTitle.trim()} className="btn-primary text-sm px-3 py-1.5">
          + Task
        </button>
      </form>
      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      <ul className="space-y-1.5">
        {list.tasks.length === 0 && (
          <li className="text-xs text-white/40 px-2 py-1">No tasks.</li>
        )}
        {list.tasks.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onOpenTask(t)}
              className="w-full text-left px-3 py-2 rounded bg-white/5 hover:bg-white/10 border border-white/10"
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <p className="text-sm text-white font-medium truncate">{t.title}</p>
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${TASK_STATUS_PILL[t.status]}`}>
                  {TASK_STATUS_LABEL[t.status]}
                </span>
              </div>
              <p className="text-[11px] text-white/50 flex items-center gap-2 flex-wrap">
                {t.assigneeName && <span>👤 {t.assigneeName}</span>}
                {t.dueDate && <DueChip date={t.dueDate} done={t.status === 'done'} />}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DueChip({ date, done }: { date: string; done: boolean }) {
  const due = new Date(date);
  const now = new Date();
  const oneDay = 86400000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((dueDay.getTime() - startOfToday.getTime()) / oneDay);
  let label: string;
  if (diffDays < 0) label = `Overdue · ${due.toLocaleDateString()}`;
  else if (diffDays === 0) label = 'Today';
  else if (diffDays === 1) label = 'Tomorrow';
  else label = due.toLocaleDateString();
  const cls = done
    ? 'text-white/40'
    : diffDays < 0
      ? 'text-red-300'
      : diffDays <= 2
        ? 'text-yellow-300'
        : 'text-white/50';
  return <span className={cls}>📅 {label}</span>;
}

export default GroupTasksPanel;
